import { useContext, useEffect } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useT } from "@/i18n/I18nProvider";

/**
 * 열린 세션의 이탈 확인 (ADR 0036 결정 4)
 * --------------------------------------------------------------------------
 * 세션은 그것을 연 화면에 매여 있습니다. 화면을 떠나면 세션이 끝나고, 그 종료는
 * **조용히 일어나지 않습니다** — 작업 중이던 셸이 라우트 이동 한 번에 사라지면
 * 사용자는 무엇을 잃었는지도 모릅니다.
 *
 * 두 경로를 각각 다른 수단이 막습니다.
 *
 *   SPA 라우트 이동   `useBlocker` + 앱 모달. 우리가 문구를 소유합니다.
 *   새로고침·탭 닫기  `beforeunload`. **브라우저가 문구를 소유합니다** — 스펙상
 *                    커스텀 문구를 띄울 수 없고, 띄우려 하면 무시됩니다.
 */

/** data router 안인지. `useBlocker`는 여기서만 동작하고, 아니면 던집니다. */
export function useInDataRouter(): boolean {
  return useContext(UNSAFE_DataRouterContext) != null;
}

/**
 * 새로고침·탭 닫기 경고. 브라우저가 기본 문구를 띄웁니다.
 *
 * 사용자가 상호작용한 적 없는 문서에서는 브라우저가 이 경고를 무시합니다(스펙).
 * 세션을 열려면 버튼을 눌러야 하므로 실제 경로에서는 늘 상호작용이 선행합니다.
 */
export function useBeforeUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      /* 구형 브라우저는 반환값을 봅니다. 문구는 어차피 무시되고 존재 여부만 셉니다. */
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);
}

/**
 * 라우트 이탈 확인 모달.
 *
 * **data router 안에서만 마운트합니다.** 훅을 조건부로 부르지 않기 위해 조건은 부모가
 * 판정하고(`useInDataRouter`), 이 컴포넌트는 항상 `useBlocker`를 부릅니다.
 */
export function ExecRouteLeaveGuard({ active, onLeave }: { active: boolean; onLeave: () => void }) {
  const { t } = useT("terminal");
  const blocker = useBlocker(active);

  /* 세션이 먼저 끝나면(셸 exit·서버 종료) 막아 둔 이동을 풀어 줍니다 — 풀지 않으면
     사용자가 이미 없는 세션 때문에 모달을 한 번 더 지나야 합니다. */
  useEffect(() => {
    if (!active && blocker.state === "blocked") blocker.reset();
  }, [active, blocker]);

  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      title={t("terminal.confirm.leave.title")}
      message={t("terminal.confirm.leave.body")}
      confirmLabel={t("terminal.confirm.leave.ok")}
      cancelLabel={t("terminal.confirm.stay")}
      testId="exec-leave-confirm"
      onConfirm={() => {
        onLeave();
        blocker.proceed?.();
      }}
      onCancel={() => blocker.reset?.()}
    />
  );
}
